import {
  AlertTriangle,
  ArrowRight,
  Calendar,
  CheckCircle2,
  Clock,
  Coins,
  History,
  Layers,
  Plus,
  RefreshCw,
  RotateCcw,
  Save,
  Trash2,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { fetchBudgetPlan, resetBudgetPlan, saveBudgetPlan } from "../api";
import { MetricCard } from "../components/MetricCard";
import { useDashboard } from "../context/DashboardContext";
import type {
  BudgetPlanDayItem,
  BudgetPlanLogItem,
  BudgetPlanPayload,
  TramoItemInput,
} from "../types";

function formatCurrency(amount?: number | null): string {
  if (amount == null) return "—";
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDateDisplay(dateStr: string): string {
  const parts = dateStr.split("-");
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return dateStr;
}

export function BudgetPlanPage() {
  const { session, data: dashboardData, selectedClient, setSelectedClient } = useDashboard();

  // Estados de control y formulario
  const [platform, setPlatform] = useState<"gads" | "meta">("gads");
  const now = new Date();
  const currentMonthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const [month, setMonth] = useState<string>(currentMonthStr);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const [planData, setPlanData] = useState<BudgetPlanPayload | null>(null);

  // Overrides locales en edición (day -> amount)
  const [localOverrides, setLocalOverrides] = useState<Record<number, number>>({});
  const [motivo, setMotivo] = useState("");

  // Editor de tramos
  const [tramoDesde, setTramoDesde] = useState("");
  const [tramoHasta, setTramoHasta] = useState("");
  const [tramoMonto, setTramoMonto] = useState<string>("");
  const [tramosList, setTramosList] = useState<TramoItemInput[]>([]);

  // Si no tiene permisos, redirigir
  if (session && !session.canEditBudgetPlan) {
    return <Navigate to="/" replace />;
  }

  // Lista de clientes disponibles desde el dashboard
  const clientList = useMemo(() => {
    return (dashboardData?.clients || []).filter((c) => c.clientSlug !== "ALL");
  }, [dashboardData]);

  // Cliente activo
  const effectiveClient = selectedClient === "ALL" ? (clientList[0]?.clientSlug || "roda") : selectedClient;

  // Carga del plan
  const loadPlan = async (clientSlug: string, plat: string, mon: string) => {
    if (!clientSlug) return;
    setLoading(true);
    setErrorMsg(null);
    setSuccessMsg(null);
    try {
      const res = await fetchBudgetPlan(clientSlug, plat, mon);
      setPlanData(res);
      // Inicializar overrides locales desde los datos existentes
      const initialMap: Record<number, number> = {};
      for (const d of res.dias) {
        if (d.es_override && d.override != null) {
          initialMap[d.day] = d.override;
        }
      }
      setLocalOverrides(initialMap);
      setTramosList([]);
    } catch (err: any) {
      setPlanData(null);
      setErrorMsg(err?.message || "No se pudo cargar el presupuesto para este cliente y plataforma.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (effectiveClient) {
      void loadPlan(effectiveClient, platform, month);
    }
  }, [effectiveClient, platform, month]);

  // Cálculos en tiempo real basados en localOverrides y ppto_mensual
  const daysInMonth = planData?.dias.length || 31;
  const pptoMensual = planData?.ppto_mensual || 0;
  const planBase = daysInMonth > 0 ? pptoMensual / daysInMonth : 0;

  const totalOverrides = useMemo(() => {
    return Object.values(localOverrides).reduce((acc, v) => acc + (v || 0), 0);
  }, [localOverrides]);

  const diasConOverrideCount = useMemo(() => {
    return Object.keys(localOverrides).length;
  }, [localOverrides]);

  const diasSinOverrideCount = daysInMonth - diasConOverrideCount;
  const remanente = pptoMensual - totalOverrides;
  const montoRestoDiario =
    remanente >= 0 && diasSinOverrideCount > 0 ? remanente / diasSinOverrideCount : null;

  const overridesExcedenPpto = remanente < 0;
  const coberturaCompleta = diasSinOverrideCount === 0;
  const cuadreCompletoInvalido =
    coberturaCompleta && Math.round(totalOverrides) !== Math.round(pptoMensual);

  const canSave =
    !loading &&
    !saving &&
    motivo.trim().length > 0 &&
    !overridesExcedenPpto &&
    !cuadreCompletoInvalido;

  // Manejo de edición por día
  const handleDayOverrideChange = (dayNum: number, rawVal: string) => {
    const trimmed = rawVal.trim();
    if (trimmed === "") {
      const next = { ...localOverrides };
      delete next[dayNum];
      setLocalOverrides(next);
      return;
    }
    const parsed = parseFloat(trimmed);
    if (!isNaN(parsed) && parsed >= 0) {
      setLocalOverrides((prev) => ({
        ...prev,
        [dayNum]: parsed,
      }));
    }
  };

  // Aplicar tramo a overrides
  const handleApplyTramo = () => {
    if (!tramoDesde || !tramoHasta || tramoMonto === "") return;
    const montoNum = parseFloat(tramoMonto);
    if (isNaN(montoNum) || montoNum < 0) return;

    const dStart = new Date(tramoDesde + "T00:00:00");
    const dEnd = new Date(tramoHasta + "T00:00:00");
    if (dStart > dEnd) {
      setErrorMsg("La fecha 'Desde' no puede ser posterior a 'Hasta'");
      return;
    }

    const next = { ...localOverrides };
    const curr = new Date(dStart);
    while (curr <= dEnd) {
      const dayNum = curr.getDate();
      next[dayNum] = montoNum;
      curr.setDate(curr.getDate() + 1);
    }

    setLocalOverrides(next);
    setTramosList((prev) => [
      ...prev,
      { desde: tramoDesde, hasta: tramoHasta, monto_diario: montoNum },
    ]);
    setTramoDesde("");
    setTramoHasta("");
    setTramoMonto("");
    setErrorMsg(null);
  };

  // Guardar overrides
  const handleSave = async () => {
    if (!canSave || !effectiveClient) return;
    setSaving(true);
    setErrorMsg(null);
    setSuccessMsg(null);

    // Preparar lista de días
    const diasArray = Object.entries(localOverrides).map(([dayNumStr, monto]) => {
      const dayNum = parseInt(dayNumStr, 10);
      const [y, m] = month.split("-");
      const dateStr = `${y}-${m}-${String(dayNum).padStart(2, "0")}`;
      return { date: dateStr, monto };
    });

    try {
      const res = await saveBudgetPlan({
        client_slug: effectiveClient,
        platform,
        month,
        motivo: motivo.trim(),
        dias: diasArray,
      });
      setSuccessMsg(res.message || "Presupuesto guardado exitosamente");
      setMotivo("");
      await loadPlan(effectiveClient, platform, month);
    } catch (err: any) {
      setErrorMsg(err?.message || "Error al guardar overrides de presupuesto.");
    } finally {
      setSaving(false);
    }
  };

  // Resetear a reparto parejo
  const handleReset = async () => {
    if (!effectiveClient) return;
    const confirm = window.confirm(
      "¿Seguro que deseas eliminar todos los overrides de este mes y volver al reparto parejo?",
    );
    if (!confirm) return;

    setSaving(true);
    setErrorMsg(null);
    setSuccessMsg(null);
    try {
      const res = await resetBudgetPlan(
        effectiveClient,
        platform,
        month,
        motivo.trim() || "Volver al reparto parejo",
      );
      setSuccessMsg(res.message || "Overrides eliminados.");
      setLocalOverrides({});
      setTramosList([]);
      setMotivo("");
      await loadPlan(effectiveClient, platform, month);
    } catch (err: any) {
      setErrorMsg(err?.message || "Error al resetear presupuesto.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page enter">
      {/* Header */}
      <section className="page-intro">
        <div>
          <span className="eyebrow">Gestión de Tráfico & Pacing · Exclusivo Dirección / Traffic</span>
          <h1>Plan de Presupuesto Diario</h1>
          <p>
            Configura tramos o días con presupuestos específicos sin tocar la planilla de KAMs.
            Los días sin override absorben automáticamente el remanente de manera equitativa.
          </p>
        </div>
      </section>

      {/* Controles de selección */}
      <section className="panel" style={{ padding: "1.25rem 1.5rem" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "1.5rem", alignItems: "flex-end" }}>
          {/* Selector Cliente */}
          <div style={{ minWidth: "220px", flex: "1 1 200px" }}>
            <label style={{ display: "block", fontSize: "0.82rem", color: "var(--muted)", marginBottom: "0.4rem" }}>
              Cliente
            </label>
            <select
              value={effectiveClient}
              onChange={(e) => {
                setSelectedClient(e.target.value);
              }}
              style={{
                width: "100%",
                padding: "0.6rem 0.8rem",
                borderRadius: "var(--radius-sm)",
                background: "var(--surface-2)",
                color: "var(--text)",
                border: "1px solid var(--line-strong)",
              }}
            >
              {clientList.map((c) => (
                <option key={c.clientSlug} value={c.clientSlug}>
                  {c.displayName || c.clientSlug}
                </option>
              ))}
            </select>
          </div>

          {/* Selector Plataforma */}
          <div style={{ minWidth: "180px" }}>
            <label style={{ display: "block", fontSize: "0.82rem", color: "var(--muted)", marginBottom: "0.4rem" }}>
              Plataforma
            </label>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <button
                type="button"
                onClick={() => setPlatform("gads")}
                style={{
                  padding: "0.6rem 1rem",
                  borderRadius: "var(--radius-sm)",
                  background: platform === "gads" ? "var(--blue)" : "var(--surface-2)",
                  color: platform === "gads" ? "#fff" : "var(--muted)",
                  border: "1px solid var(--line)",
                  cursor: "pointer",
                  fontWeight: platform === "gads" ? 600 : 400,
                }}
              >
                Google Ads
              </button>
              <button
                type="button"
                onClick={() => setPlatform("meta")}
                style={{
                  padding: "0.6rem 1rem",
                  borderRadius: "var(--radius-sm)",
                  background: platform === "meta" ? "var(--blue)" : "var(--surface-2)",
                  color: platform === "meta" ? "#fff" : "var(--muted)",
                  border: "1px solid var(--line)",
                  cursor: "pointer",
                  fontWeight: platform === "meta" ? 600 : 400,
                }}
              >
                Meta Ads
              </button>
            </div>
          </div>

          {/* Selector Mes */}
          <div style={{ minWidth: "160px" }}>
            <label style={{ display: "block", fontSize: "0.82rem", color: "var(--muted)", marginBottom: "0.4rem" }}>
              Mes
            </label>
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              style={{
                width: "100%",
                padding: "0.55rem 0.8rem",
                borderRadius: "var(--radius-sm)",
                background: "var(--surface-2)",
                color: "var(--text)",
                border: "1px solid var(--line-strong)",
              }}
            />
          </div>

          {/* Botón Refrescar */}
          <div>
            <button
              type="button"
              onClick={() => void loadPlan(effectiveClient, platform, month)}
              disabled={loading}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.4rem",
                padding: "0.6rem 1rem",
                borderRadius: "var(--radius-sm)",
                background: "var(--surface-3)",
                color: "var(--text)",
                border: "1px solid var(--line)",
                cursor: "pointer",
              }}
            >
              <RefreshCw size={14} className={loading ? "spin" : ""} />
              Actualizar
            </button>
          </div>
        </div>
      </section>

      {/* Alertas de error y éxito */}
      {errorMsg && (
        <div
          className="panel"
          style={{
            background: "rgba(255, 102, 94, 0.12)",
            border: "1px solid var(--red)",
            color: "var(--red)",
            padding: "0.9rem 1.25rem",
            display: "flex",
            alignItems: "center",
            gap: "0.6rem",
          }}
        >
          <AlertTriangle size={18} />
          <span>{errorMsg}</span>
        </div>
      )}

      {successMsg && (
        <div
          className="panel"
          style={{
            background: "rgba(75, 212, 160, 0.12)",
            border: "1px solid var(--green)",
            color: "var(--green)",
            padding: "0.9rem 1.25rem",
            display: "flex",
            alignItems: "center",
            gap: "0.6rem",
          }}
        >
          <CheckCircle2 size={18} />
          <span>{successMsg}</span>
        </div>
      )}

      {/* KPI Cards del Mes */}
      {planData && (
        <section
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "1rem",
          }}
        >
          <MetricCard
            label="Presupuesto Mensual"
            value={formatCurrency(pptoMensual)}
            note="Desde Google Sheets (KAM)"
            icon={Coins}
            tone="neutral"
          />
          <MetricCard
            label="Plan Base Parejo"
            value={formatCurrency(planBase)}
            note={`${daysInMonth} días en el mes`}
            icon={Calendar}
            tone="neutral"
          />
          <MetricCard
            label="Total Overrides Asignados"
            value={formatCurrency(totalOverrides)}
            note={`${diasConOverrideCount} de ${daysInMonth} días con override`}
            icon={Layers}
            tone={overridesExcedenPpto ? "danger" : diasConOverrideCount > 0 ? "accent" : "neutral"}
          />
          <MetricCard
            label="Remanente por Distribuir"
            value={formatCurrency(remanente)}
            note={
              diasSinOverrideCount > 0
                ? `${formatCurrency(montoRestoDiario)} / día sin override`
                : "Mes con cobertura 100%"
            }
            icon={remanente < 0 ? TrendingDown : TrendingUp}
            tone={remanente < 0 ? "danger" : remanente === 0 ? "good" : "warning"}
          />
        </section>
      )}

      {/* Formulario de Tramos y Guardado */}
      {planData && (
        <section className="panel" style={{ padding: "1.25rem 1.5rem" }}>
          <div className="panel-heading" style={{ marginBottom: "1rem" }}>
            <div>
              <span className="eyebrow">Edición masiva</span>
              <h2 style={{ fontSize: "1.2rem", margin: 0 }}>Editor de Tramos</h2>
              <p className="panel-copy">Aplica un presupuesto diario fijo a un bloque consecutivo de días.</p>
            </div>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem", alignItems: "flex-end" }}>
            <div>
              <label style={{ display: "block", fontSize: "0.8rem", color: "var(--muted)", marginBottom: "0.3rem" }}>
                Desde
              </label>
              <input
                type="date"
                value={tramoDesde}
                onChange={(e) => setTramoDesde(e.target.value)}
                style={{
                  padding: "0.55rem 0.8rem",
                  borderRadius: "var(--radius-sm)",
                  background: "var(--surface-2)",
                  color: "var(--text)",
                  border: "1px solid var(--line)",
                }}
              />
            </div>

            <div>
              <label style={{ display: "block", fontSize: "0.8rem", color: "var(--muted)", marginBottom: "0.3rem" }}>
                Hasta
              </label>
              <input
                type="date"
                value={tramoHasta}
                onChange={(e) => setTramoHasta(e.target.value)}
                style={{
                  padding: "0.55rem 0.8rem",
                  borderRadius: "var(--radius-sm)",
                  background: "var(--surface-2)",
                  color: "var(--text)",
                  border: "1px solid var(--line)",
                }}
              />
            </div>

            <div>
              <label style={{ display: "block", fontSize: "0.8rem", color: "var(--muted)", marginBottom: "0.3rem" }}>
                Monto diario (CLP)
              </label>
              <input
                type="number"
                min="0"
                step="1000"
                placeholder="Ej. 280000"
                value={tramoMonto}
                onChange={(e) => setTramoMonto(e.target.value)}
                style={{
                  padding: "0.55rem 0.8rem",
                  borderRadius: "var(--radius-sm)",
                  background: "var(--surface-2)",
                  color: "var(--text)",
                  border: "1px solid var(--line)",
                  width: "160px",
                }}
              />
            </div>

            <button
              type="button"
              onClick={handleApplyTramo}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.4rem",
                padding: "0.6rem 1.1rem",
                borderRadius: "var(--radius-sm)",
                background: "var(--blue)",
                color: "#fff",
                border: "none",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              <Plus size={15} />
              Aplicar Tramo
            </button>
          </div>

          {/* Barra de Motivo y Acciones de Guardado */}
          <div
            style={{
              marginTop: "1.5rem",
              paddingTop: "1.25rem",
              borderTop: "1px solid var(--line)",
              display: "flex",
              flexWrap: "wrap",
              gap: "1.5rem",
              alignItems: "flex-end",
              justifyContent: "space-between",
            }}
          >
            <div style={{ flex: "1 1 320px" }}>
              <label style={{ display: "block", fontSize: "0.82rem", color: "var(--muted)", marginBottom: "0.4rem" }}>
                Motivo del ajuste <span style={{ color: "var(--red)" }}>* (Obligatorio para auditoría)</span>
              </label>
              <input
                type="text"
                placeholder="Ej. Escalado Cyber Monday días 2 al 7 según acuerdo con cliente"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                style={{
                  width: "100%",
                  padding: "0.6rem 0.8rem",
                  borderRadius: "var(--radius-sm)",
                  background: "var(--surface-2)",
                  color: "var(--text)",
                  border: "1px solid var(--line-strong)",
                }}
              />
            </div>

            <div style={{ display: "flex", gap: "0.8rem", alignItems: "center" }}>
              <button
                type="button"
                onClick={handleReset}
                disabled={saving || diasConOverrideCount === 0}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.4rem",
                  padding: "0.65rem 1.1rem",
                  borderRadius: "var(--radius-sm)",
                  background: "transparent",
                  color: "var(--muted)",
                  border: "1px solid var(--line)",
                  cursor: diasConOverrideCount > 0 ? "pointer" : "not-allowed",
                  opacity: diasConOverrideCount > 0 ? 1 : 0.5,
                }}
              >
                <RotateCcw size={15} />
                Volver al reparto parejo
              </button>

              <button
                type="button"
                onClick={handleSave}
                disabled={!canSave}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.4rem",
                  padding: "0.65rem 1.3rem",
                  borderRadius: "var(--radius-sm)",
                  background: canSave ? "var(--green)" : "var(--surface-3)",
                  color: canSave ? "#000" : "var(--muted)",
                  border: "none",
                  fontWeight: 600,
                  cursor: canSave ? "pointer" : "not-allowed",
                  boxShadow: canSave ? "0 4px 14px rgba(75, 212, 160, 0.3)" : "none",
                }}
              >
                <Save size={15} />
                {saving ? "Guardando…" : "Guardar Cambios"}
              </button>
            </div>
          </div>

          {/* Alertas de Validación en tiempo real */}
          {overridesExcedenPpto && (
            <p style={{ color: "var(--red)", fontSize: "0.85rem", marginTop: "0.8rem", marginBottom: 0 }}>
              ⚠️ La suma de overrides ({formatCurrency(totalOverrides)}) supera el presupuesto mensual ({formatCurrency(pptoMensual)}). Reduce montos antes de guardar.
            </p>
          )}

          {cuadreCompletoInvalido && (
            <p style={{ color: "var(--red)", fontSize: "0.85rem", marginTop: "0.8rem", marginBottom: 0 }}>
              ⚠️ Todos los días del mes tienen override fijado, pero la suma ({formatCurrency(totalOverrides)}) no iguala el presupuesto mensual ({formatCurrency(pptoMensual)}).
            </p>
          )}

          {!overridesExcedenPpto && !cuadreCompletoInvalido && motivo.trim().length === 0 && (
            <p style={{ color: "var(--muted)", fontSize: "0.82rem", marginTop: "0.8rem", marginBottom: 0 }}>
              * Ingresa un motivo para habilitar el guardado.
            </p>
          )}
        </section>
      )}

      {/* Tabla Diaria */}
      {planData && (
        <section className="panel" style={{ padding: "1.25rem 1.5rem" }}>
          <div className="panel-heading" style={{ marginBottom: "1rem" }}>
            <div>
              <span className="eyebrow">Detalle y edición directa</span>
              <h2 style={{ fontSize: "1.2rem", margin: 0 }}>Calendario Diario de Presupuesto</h2>
              <p className="panel-copy">
                Edita el override directamente por día o déjalo vacío para recibir el remanente equitativo.
              </p>
            </div>
            <span className="count-badge">
              {diasConOverrideCount} intervenidos / {diasSinOverrideCount} base
            </span>
          </div>

          <div className="table-scroll">
            <table className="usage-table compact" style={{ width: "100%" }}>
              <thead>
                <tr>
                  <th>Día</th>
                  <th>Fecha</th>
                  <th className="numeric">Plan Base</th>
                  <th style={{ minWidth: "150px" }}>Override Traffic</th>
                  <th className="numeric">Plan Efectivo</th>
                  <th className="numeric">Gasto Real</th>
                  <th className="numeric">Diferencia</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {planData.dias.map((d) => {
                  const hasLocal = localOverrides[d.day] !== undefined;
                  const currentVal = hasLocal ? localOverrides[d.day] : "";
                  const effectivePlan = hasLocal ? localOverrides[d.day] : montoRestoDiario;
                  const diff =
                    effectivePlan != null ? Math.round(d.spend_real - effectivePlan) : null;

                  return (
                    <tr
                      key={d.date}
                      style={{
                        background: hasLocal ? "rgba(125, 150, 255, 0.05)" : undefined,
                      }}
                    >
                      <td style={{ fontWeight: 600 }}>Día {d.day}</td>
                      <td>{formatDateDisplay(d.date)}</td>
                      <td className="numeric" style={{ color: "var(--muted)" }}>
                        {formatCurrency(d.plan_base)}
                      </td>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                          <input
                            type="number"
                            min="0"
                            step="1000"
                            placeholder="Reparto parejo"
                            value={currentVal}
                            onChange={(e) => handleDayOverrideChange(d.day, e.target.value)}
                            style={{
                              width: "130px",
                              padding: "0.35rem 0.6rem",
                              borderRadius: "var(--radius-sm)",
                              background: hasLocal ? "rgba(125, 150, 255, 0.15)" : "var(--surface-2)",
                              border: hasLocal ? "1px solid var(--blue)" : "1px solid var(--line)",
                              color: "var(--text)",
                              fontSize: "0.88rem",
                            }}
                          />
                          {hasLocal && (
                            <button
                              type="button"
                              title="Quitar override de este día"
                              onClick={() => handleDayOverrideChange(d.day, "")}
                              style={{
                                background: "transparent",
                                border: "none",
                                color: "var(--muted)",
                                cursor: "pointer",
                                padding: "2px",
                              }}
                            >
                              <Trash2 size={13} />
                            </button>
                          )}
                        </div>
                      </td>
                      <td
                        className="numeric"
                        style={{
                          fontWeight: 600,
                          color: hasLocal ? "var(--blue)" : "var(--text)",
                        }}
                      >
                        {formatCurrency(effectivePlan)}
                      </td>
                      <td className="numeric">{formatCurrency(d.spend_real)}</td>
                      <td
                        className="numeric"
                        style={{
                          fontWeight: 600,
                          color:
                            diff == null
                              ? "var(--muted)"
                              : diff > 0
                              ? "var(--red)"
                              : "var(--green)",
                        }}
                      >
                        {diff != null ? `${diff > 0 ? "+" : ""}${formatCurrency(diff)}` : "—"}
                      </td>
                      <td>
                        {hasLocal ? (
                          <span
                            style={{
                              display: "inline-block",
                              padding: "0.15rem 0.5rem",
                              borderRadius: "999px",
                              fontSize: "0.72rem",
                              background: "rgba(125, 150, 255, 0.2)",
                              color: "var(--blue)",
                              fontWeight: 600,
                            }}
                          >
                            Override
                          </span>
                        ) : (
                          <span
                            style={{
                              display: "inline-block",
                              padding: "0.15rem 0.5rem",
                              borderRadius: "999px",
                              fontSize: "0.72rem",
                              background: "rgba(255, 255, 255, 0.05)",
                              color: "var(--muted)",
                            }}
                          >
                            Equitativo
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Historial de Auditoría desde el Log */}
      {planData && planData.logs && planData.logs.length > 0 && (
        <section className="panel" style={{ padding: "1.25rem 1.5rem" }}>
          <div className="panel-heading" style={{ marginBottom: "1rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
              <History size={18} style={{ color: "var(--blue)" }} />
              <div>
                <span className="eyebrow">Auditoría inmutable</span>
                <h2 style={{ fontSize: "1.1rem", margin: 0 }}>Historial de Cambios</h2>
              </div>
            </div>
            <span className="count-badge">{planData.logs.length} registros</span>
          </div>

          <div className="table-scroll">
            <table className="usage-table compact" style={{ width: "100%" }}>
              <thead>
                <tr>
                  <th>Fecha y Hora</th>
                  <th>Usuario</th>
                  <th>Motivo</th>
                  <th>Antes</th>
                  <th>Después</th>
                </tr>
              </thead>
              <tbody>
                {planData.logs.map((log) => (
                  <tr key={log.id}>
                    <td style={{ whiteSpace: "nowrap", color: "var(--muted)", fontSize: "0.82rem" }}>
                      {log.ts.replace("T", " ").substring(0, 19)}
                    </td>
                    <td style={{ fontWeight: 500 }}>{log.user_email}</td>
                    <td style={{ color: "var(--text)" }}>{log.motivo}</td>
                    <td style={{ fontSize: "0.8rem", color: "var(--muted)" }}>
                      {Array.isArray(log.antes) && log.antes.length > 0
                        ? `${log.antes.length} días con override`
                        : "Reparto parejo"}
                    </td>
                    <td style={{ fontSize: "0.8rem", color: "var(--blue)", fontWeight: 500 }}>
                      {Array.isArray(log.despues) && log.despues.length > 0
                        ? `${log.despues.length} días ajustados`
                        : "Reparto parejo"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
